/**
 * 路由配置文件
 *
 * 集中管理所有路由配置，方便维护和扩展
 */

import { RouteRecordRaw } from "vue-router";
import { useCapabilityStore, useSimpleAuthStore } from "@/stores";
import { canEnterCdss } from "@/config/workstations";
import { readLastWorkstation, resolveHomeRoute } from "@shared/utils/homeRoute";

// DDD 模块路由
import { chargingRoutes } from "@charging/routes";
import { clinicConfigRoutes } from "@clinic-config/routes";
import { integrationRoutes } from "@integration/routes";
import { clinicalRecordRoutes } from "@clinical-record/routes";
import { frontSheetRoutes } from "@clinical-record/front-sheet/routes";
import { healthManagementRoutes } from "@health-management/routes";
import { patientManagementRoutes } from "@patient-management/routes";
import { pharmacyRoutes } from "@pharmacy/routes";
import { publicHealthRoutes } from "@public-health/routes";

/**
 * 基础路由配置
 */
export const basicRoutes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "Home",
    component: () => import("@/views/HomeRedirect.vue"),
    meta: { requiresAuth: true, hidden: true },
  },
  {
    path: "/404",
    name: "NotFound",
    component: () => import("@/components/ui/404.vue"),
    meta: {
      title: "页面未找到 - 404",
      hidden: true,
      keepAlive: false,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
    meta: { hidden: true },
  },
];

/**
 * 主要功能路由
 *
 * DDD 模块架构：页面组件从 @patient-management 等业务模块导入；
 * 下述路径为应用对外稳定路径。
 */
export const mainRoutes: RouteRecordRaw[] = [
  {
    path: "/medical",
    name: "Medical",
    component: () => import("@/views/index.vue"),
    beforeEnter: async (to) => {
      if (to.query.embed === "1" || to.query.mode === "embed") return true;
      const capabilityStore = useCapabilityStore();
      const authStore = useSimpleAuthStore();
      try {
        await capabilityStore.load();
      } catch {
        return true;
      }
      const gates = {
        has: (code: string) => capabilityStore.has(code),
        role: authStore.user?.role ?? null,
        codes: capabilityStore.capabilityCodes,
      };
      if (canEnterCdss(gates)) return true;
      const next = resolveHomeRoute({
        embed: false,
        role: gates.role,
        has: gates.has,
        codes: gates.codes,
        lastWorkstation: readLastWorkstation(),
      });
      if (!next || next === "/medical" || next.startsWith("/medical?")) {
        return true;
      }
      return next;
    },
    meta: {
      title: "决策支持系统 - 蒲公英医学决策支持系统",
      icon: "el-icon-medicine-box",
      requiresAuth: true,
      keepAlive: true,
      breadcrumb: ["决策支持系统"],
    },
  },
  {
    path: "/outpatient",
    name: "Outpatient",
    component: () =>
      import("@patient-management/interfaces/OutpatientStationView.vue"),
    meta: {
      title: "门诊信息系统 - 蒲公英医学决策支持系统",
      icon: "el-icon-user-filled",
      requiresAuth: true,
      keepAlive: true,
      breadcrumb: ["门诊信息系统"],
    },
  },
  {
    path: "/record-writing",
    name: "RecordWriting",
    component: () =>
      import("@record-writing/interfaces/RecordWritingWorkspaceView.vue"),
    meta: {
      title: "病历书写系统 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: true,
      breadcrumb: ["病历书写系统"],
    },
  },
  {
    path: "/registration-desk",
    name: "RegistrationDesk",
    component: () =>
      import("@patient-management/interfaces/RegistrationDeskView.vue"),
    meta: {
      title: "挂号收费窗口 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["挂号收费窗口"],
    },
  },
  {
    path: "/inpatient",
    name: "Inpatient",
    component: () => import("@inpatient/interfaces/InpatientWorkspaceView.vue"),
    meta: {
      title: "住院医生工作站 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["住院医生工作站"],
    },
  },
  {
    // 护士工作站：独立授权（nursing 模块），与住院医生站解耦；
    // 页面内以 capabilityStore.has("nursing") 做无权益兜底提示。
    path: "/nurse",
    name: "NurseStation",
    component: () => import("@nursing/interfaces/NurseStationView.vue"),
    meta: {
      title: "住院护士工作站 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["住院护士工作站"],
    },
  },
  {
    // 药师审方台（M1）：待审药品医嘱 → 通过/驳回（驳回必填理由）
    path: "/pharmacy-review",
    name: "PharmacyReview",
    component: () => import("@pharmacy/interfaces/PharmacyReviewView.vue"),
    meta: {
      title: "药师审方工作站 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["药师审方工作站"],
    },
  },
  {
    // 门诊分诊台（M1）：号源余量、停复号、占号/释放与四级分诊
    path: "/triage-station",
    name: "TriageStation",
    component: () =>
      import("@patient-management/interfaces/TriageStationView.vue"),
    meta: {
      title: "门诊分诊工作站 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["门诊分诊工作站"],
    },
  },
  {
    // 住院文书（M2）：入院/病程/出院记录清单、草稿签署与出院闭环校验
    path: "/inpatient-documents",
    name: "InpatientDocuments",
    component: () => import("@inpatient/interfaces/InpatientDocumentsView.vue"),
    meta: {
      title: "住院文书工作站 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["住院文书工作站"],
    },
  },
  {
    // 条码核对（M3）：患者腕带/标本/药品核对，成功与失败均留痕
    path: "/barcode-verify",
    name: "BarcodeVerify",
    component: () => import("@nursing/interfaces/BarcodeVerifyView.vue"),
    meta: {
      title: "条码核对工作站 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["条码核对工作站"],
    },
  },
  {
    // 质量看板（M5）：质量指标计算、快照查询与评级抽数 CSV 导出
    path: "/quality-dashboard",
    name: "QualityDashboard",
    component: () => import("@quality/interfaces/QualityDashboardView.vue"),
    meta: {
      title: "医疗质量管理工作站 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      breadcrumb: ["医疗质量管理工作站"],
    },
  },
  {
    // 教学自学工作区：与临床首页完全隔离的懒加载路由（T18）
    path: "/teach",
    name: "Teach",
    component: () => import("@teach/interfaces/TeachWorkspaceView.vue"),
    meta: {
      title: "教学自学 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      hidden: true,
      breadcrumb: ["教学自学"],
    },
  },
  {
    // 等保三级三员分立：平台管理角色（系统/安全/审计管理员）落地说明页。
    // 守卫见 router/index.ts：三员访问业务页面统一重定向到本页，避免级联 403。
    path: "/platform-role-notice",
    name: "PlatformRoleNotice",
    component: () => import("@/views/PlatformRoleNotice.vue"),
    meta: {
      title: "平台管理角色 - 蒲公英医学决策支持系统",
      requiresAuth: true,
      keepAlive: false,
      hidden: true,
      breadcrumb: ["平台管理角色"],
    },
  },
  {
    path: "/management",
    name: "Management",
    component: () => import("@/views/management/ManagementLayout.vue"),
    redirect: "/management/queue",
    meta: {
      title: "管理功能",
      icon: "el-icon-setting",
      requiresAuth: true,
      keepAlive: true,
      breadcrumb: ["管理"],
    },
    children: [
      {
        path: "queue",
        name: "QueueManagement",
        // 使用 DDD 模块组件 - 队列管理
        component: () =>
          import("@patient-management/queue/interfaces/QueuePanel.vue"),
        meta: {
          title: "队列管理 - 蒲公英医学决策支持系统",
          keepAlive: true,
          breadcrumb: ["管理", "队列管理"],
        },
      },
      {
        path: "patient",
        name: "PatientManagementRoute",
        // 使用 DDD 模块组件 - 患者管理
        component: () =>
          import(
            "@patient-management/shared/interfaces/PatientManagementView.vue"
          ),
        meta: {
          title: "患者管理 - 蒲公英医学决策支持系统",
          keepAlive: true,
          breadcrumb: ["管理", "患者管理"],
        },
      },
      {
        path: "record",
        name: "RecordManagement",
        // 使用 DDD 模块组件 - 病历管理
        component: () =>
          import("@clinical-record/interfaces/views/MedicalRecordView.vue"),
        meta: {
          title: "病历管理 - 蒲公英医学决策支持系统",
          keepAlive: true,
          breadcrumb: ["管理", "病历管理"],
        },
      },
      {
        path: "tag",
        name: "TagManagement",
        // 使用 DDD 模块组件 - 标签管理
        component: () =>
          import("@patient-management/tag/interfaces/TagManage.vue"),
        meta: {
          title: "标签管理 - 蒲公英医学决策支持系统",
          keepAlive: true,
          breadcrumb: ["管理", "标签管理"],
        },
      },
      {
        path: "drug-catalog",
        name: "DrugCatalogManage",
        // 使用 DDD 模块组件 - 药品目录管理（hospital_admin/super_admin 可维护）
        component: () =>
          import(
            "@patient-management/drug-catalog/interfaces/DrugCatalogManage.vue"
          ),
        meta: {
          title: "药品目录管理 - 蒲公英医学决策支持系统",
          keepAlive: true,
          breadcrumb: ["管理", "药品目录"],
        },
      },
      {
        path: "service-catalog",
        name: "ServiceCatalogManage",
        // 使用 DDD 模块组件 - 服务目录管理（迁移 0032；hospital_admin/super_admin 可维护）
        component: () =>
          import(
            "@patient-management/service-catalog/interfaces/ServiceCatalogManage.vue"
          ),
        meta: {
          title: "服务目录管理 - 蒲公英医学决策支持系统",
          keepAlive: true,
          breadcrumb: ["管理", "服务目录"],
        },
      },
    ],
  },
];

/**
 * 认证相关路由
 */
export const authRoutes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/Login.vue"),
    meta: {
      title: "登录 - 蒲公英医学决策支持系统",
      icon: "el-icon-user",
      hidden: true,
      guest: true,
      keepAlive: false,
    },
  },
  {
    path: "/download-app",
    name: "DesktopAppDownload",
    component: () => import("@/views/DesktopAppDownload.vue"),
    meta: {
      title: "桌面客户端下载 - 蒲公英医学决策支持系统",
      hidden: true,
      guest: true,
      keepAlive: false,
    },
  },
  {
    path: "/login/set-password",
    name: "SetPassword",
    component: () => import("@/views/SetPassword.vue"),
    meta: {
      title: "设置密码 - 蒲公英医学决策支持系统",
      hidden: true,
      guest: true,
      keepAlive: false,
    },
  },
  {
    path: "/his-sso",
    name: "HisSsoCallback",
    component: () => import("@/views/HisSsoCallback.vue"),
    meta: {
      title: "HIS 免密登录 - 蒲公英医学决策支持系统",
      hidden: true,
      keepAlive: false,
    },
  },
];

/**
 * 开发环境专用路由
 */
export const devRoutes: RouteRecordRaw[] = [];

/**
 * 合并所有路由
 */
export const routes: RouteRecordRaw[] = [
  ...basicRoutes,
  ...mainRoutes,
  ...authRoutes,
  ...devRoutes,
  // DDD 模块路由
  ...clinicalRecordRoutes,
  ...patientManagementRoutes,
  ...publicHealthRoutes,
  ...chargingRoutes,
  ...pharmacyRoutes,
  ...healthManagementRoutes,
  ...frontSheetRoutes,
  ...clinicConfigRoutes,
  ...integrationRoutes,
];

/**
 * 导出默认路由配置
 */
export default routes;
